'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const learnFeatures = [
    {
        title: 'Program Discovery',
        desc: 'Browse and enroll in programs across subjects, levels, and formats — all in one place.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.75" />
                <path d="M13.5 13.5l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'Live Classes',
        desc: 'Join real-time sessions with instructors — interactive, scheduled, and recorded for review.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M8 8l5 2.5L8 13V8z" fill="currentColor" />
            </svg>
        ),
    },
    {
        title: 'Progress Tracking',
        desc: 'Visualize academic growth with detailed dashboards that show exactly where you stand.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 14l4-5 3 3 4-6 3 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="1" y="1" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
        ),
    },
    {
        title: 'Tutor Connection',
        desc: 'Find and book qualified tutors based on subject, availability, and learning style.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="8" cy="7" r="3" stroke="currentColor" strokeWidth="1.75" />
                <path d="M2 18c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M15 9l2 2 3-3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Assessments',
        desc: 'Take structured tests and quizzes that measure real understanding, not just completion.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="2" width="14" height="17" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M7 7h6M7 11h4M7 15h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'Learning Community',
        desc: 'Engage with peers, share resources, and participate in collaborative learning spaces.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="14" cy="7" r="2" stroke="currentColor" strokeWidth="1.5" />
                <path d="M1 17c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M14 12c2 0 4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
        ),
    },
];

export default function WislyLearnSection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            id="wisly-learn"
            ref={sectionRef}
            className="py-28 px-6 md:px-10 relative overflow-hidden"
            style={{ background: '#fff' }}
        >
            {/* Background accent */}
            <div
                className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none"
                style={{ background: 'radial-gradient(circle at 80% 20%, rgba(55,87,255,0.06) 0%, transparent 70%)' }}
            />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Split layout */}
                <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
                    {/* Left: text */}
                    <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                            style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                        >
                            Wisly Learn
                        </div>
                        <h2
                            className="font-display font-800 mb-5"
                            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Redefining how
                            <br />
                            <span style={{ color: BRAND }}>you learn.</span>
                        </h2>
                        <p className="text-lg leading-relaxed mb-4" style={{ color: '#555' }}>
                            Wisly Learn gives students and parents a connected space for discovering, accessing, and engaging with education.
                        </p>
                        <p className="text-base leading-relaxed mb-8" style={{ color: '#777' }}>
                            From learning content and live classes to academic progress and communication with educators, Wisly Learn brings the learning experience into one intuitive environment.
                        </p>

                        {/* Tagline */}
                        <div
                            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl mb-8"
                            style={{ background: 'rgba(55,87,255,0.06)', border: '1px solid rgba(55,87,255,0.12)' }}
                        >
                            {['Discover', 'Learn', 'Connect', 'Grow'].map((word, i) => (
                                <React.Fragment key={word}>
                                    <span className="text-sm font-700" style={{ color: BRAND }}>{word}</span>
                                    {i < 3 && <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(55,87,255,0.3)' }} />}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <a
                                href="/learn"
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-700 text-white transition-all duration-300"
                                style={{ background: BRAND, boxShadow: '0 8px 24px rgba(55,87,255,0.25)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                            >
                                Learn More
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Right: visual card */}
                    <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="rounded-3xl p-8 relative overflow-hidden"
                            style={{ background: 'linear-gradient(135deg, rgba(55,87,255,0.08) 0%, rgba(55,87,255,0.03) 100%)', border: '1px solid rgba(55,87,255,0.12)' }}
                        >
                            {/* Mock UI card */}
                            <div className="rounded-2xl p-5 mb-4" style={{ background: 'white', boxShadow: '0 4px 24px rgba(55,87,255,0.08)' }}>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <p className="text-xs font-700 uppercase tracking-widest mb-1" style={{ color: '#999', letterSpacing: '0.12em' }}>Current Progress</p>
                                        <p className="text-lg font-800" style={{ color: '#0D0D0D' }}>Mathematics — Grade 10</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M3 14l4-5 3 3 4-6 3 4" stroke={BRAND} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="w-full h-2 rounded-full mb-2" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                    <div className="h-2 rounded-full" style={{ width: '72%', background: BRAND }} />
                                </div>
                                <p className="text-xs font-600" style={{ color: '#888' }}>72% complete · 3 modules remaining</p>
                            </div>

                            {/* Upcoming session */}
                            <div className="rounded-2xl p-5 mb-4" style={{ background: 'white', boxShadow: '0 4px 24px rgba(55,87,255,0.08)' }}>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BRAND }}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="white" strokeWidth="1.5" />
                                            <path d="M5 1v3M11 1v3M2 7h12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-700" style={{ color: '#0D0D0D' }}>Live Session: Algebra Fundamentals</p>
                                        <p className="text-xs font-500" style={{ color: '#888' }}>Today at 3:00 PM · Mr. Osei</p>
                                    </div>
                                </div>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-3 gap-3">
                                {[{ label: 'Programs', value: '12' }, { label: 'Hours', value: '48' }, { label: 'Score', value: '94%' }].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-4 text-center" style={{ background: 'white', boxShadow: '0 2px 12px rgba(55,87,255,0.06)' }}>
                                        <p className="text-xl font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                        <p className="text-xs font-600" style={{ color: '#888' }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features grid */}
                <div>
                    <p className="text-xs font-700 uppercase tracking-widest mb-8" style={{ color: '#999', letterSpacing: '0.15em' }}>What Wisly Learn includes</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {learnFeatures.map((feature, i) => (
                            <div
                                key={feature.title}
                                className={`rounded-2xl p-6 group cursor-pointer transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{
                                    background: '#FAFAFA',
                                    border: '1px solid rgba(55,87,255,0.08)',
                                    transitionDelay: `${i * 60}ms`,
                                }}
                                onMouseEnter={e => {
                                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.25)';
                                    (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.03)';
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                                }}
                                onMouseLeave={e => {
                                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)';
                                    (e.currentTarget as HTMLElement).style.background = '#FAFAFA';
                                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                                }}
                            >
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-all duration-300"
                                    style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}
                                >
                                    {feature.icon}
                                </div>
                                <h4 className="text-base font-700 mb-2" style={{ color: '#0D0D0D', letterSpacing: '-0.01em' }}>{feature.title}</h4>
                                <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
